import { config } from '../config';

/**
 * Privacy-first analytics (Plausible: cookieless, no consent banner needed).
 * Rules: never send names, emails, or free text; never send assessment answers
 * or levels — only the assessment id, so survivors are never profiled.
 */
export type AnalyticsEvent =
  | 'lead_submit'
  | 'guide_download'
  | 'speaking_cta_click'
  | 'one_sheet_download'
  | 'assessment_complete';

type Props = Record<string, string | number | boolean>;

declare global {
  interface Window { plausible?: (event: string, options?: { props?: Props }) => void }
}

const SCRIPT_SRC = 'https://plausible.io/js/script.js';

/** Inject the analytics script once, only when an id is configured. Safe to call repeatedly. */
export function initAnalytics(): void {
  const domain = config.analyticsId;
  if (!domain || typeof document === 'undefined') return;
  if (document.querySelector(`script[data-domain="${domain}"]`)) return;
  const s = document.createElement('script');
  s.defer = true;
  s.setAttribute('data-domain', domain);
  s.src = SCRIPT_SRC;
  document.head.appendChild(s);
}

/** Fire a custom event. No-op when analytics is disabled or the script hasn't loaded. Never throws. */
export function track(event: AnalyticsEvent, props?: Props): void {
  try {
    if (!config.analyticsId || typeof window === 'undefined' || typeof window.plausible !== 'function') return;
    window.plausible(event, props ? { props } : undefined);
  } catch { /* analytics must never break the page */ }
}
